import { CheckIcon } from './icons';

export type WizardStep = 'connect' | 'settings' | 'products' | 'import' | 'done';

export const STEPS: { id: WizardStep; label: string; n: number; }[] = [
  { id: 'connect', label: 'Connect Stripe', n: 0 },
  { id: 'settings', label: 'Sync settings', n: 1 },
  { id: 'products', label: 'Map products', n: 2 },
  { id: 'import', label: 'Import rules', n: 3 },
  { id: 'done', label: 'Done', n: 4 },
];

export function StepIndicator({
  currentStep,
  onStepClick,
}: {
  currentStep: WizardStep;
  /**
   * Optional navigation for COMPLETED steps (2026-09-18): the wizard wires
   * this so a completed "Connect Stripe" pill can be clicked to revisit the
   * connect step — the only place the "Use a different account" escape hatch
   * (wrong/revoked account) is reachable from. Future steps can opt in the
   * same way; by default the indicator stays display-only.
   */
  onStepClick?: (step: WizardStep) => void;
}) {
  const currentN = STEPS.find((s) => s.id === currentStep)?.n ?? 0;
  return (
    <nav aria-label="Progress" className="flex flex-wrap items-center gap-2">
      {STEPS.map((step, i) => {
        const isDone = step.n < currentN;
        const isActive = step.n === currentN;
        const clickable = isDone && step.id === 'connect' && !!onStepClick;
        const pill = (
          <div
            className={[
              'flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              isDone
                ? 'bg-success-500/10 text-success-600'
                : isActive
                  ? 'bg-brand-600 text-white'
                  : 'bg-ink-100 text-ink-500',
              clickable ? 'cursor-pointer hover:ring-2 hover:ring-success-400' : '',
            ].join(' ')}
          >
            <span
              className={[
                'flex h-5 w-5 items-center justify-center rounded-full text-xs',
                isDone ? 'bg-success-500 text-white' : isActive ? 'bg-white/25' : 'bg-ink-300 text-white',
              ].join(' ')}
            >
              {isDone ? <CheckIcon className="h-3 w-3" /> : step.n + 1}
            </span>
            {step.label}
          </div>
        );
        return (
          <div key={step.id} className="flex items-center gap-2">
            {clickable ? (
              <button
                type="button"
                onClick={() => onStepClick?.(step.id)}
                title="Back to connection settings"
                className="rounded-full"
              >
                {pill}
              </button>
            ) : (
              pill
            )}
            {i < STEPS.length - 1 && (
              <span className="text-ink-300" aria-hidden="true">
                →
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
